const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  month: 'short',
  day: 'numeric',
})

const LONG_DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})

const SHORT_DATE_WITH_YEAR_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const FULL_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const DAY_HEADER_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

const WEEKDAY_LONG_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
})

export const parseDateOrNull = (value: string): Date | null => {
  const trimmed = value.trim()
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (ymd) {
    const y = Number(ymd[1])
    const m = Number(ymd[2]) - 1
    const d = Number(ymd[3])
    const local = new Date(y, m, d)
    if (Number.isNaN(local.getTime())) return null
    if (local.getFullYear() !== y || local.getMonth() !== m || local.getDate() !== d) return null
    return local
  }
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

export const addMinutes = (value: Date, minutes: number): Date => {
  const copy = new Date(value)
  copy.setMinutes(copy.getMinutes() + minutes)
  return copy
}

export const roundToNextQuarterHour = (value: Date): Date => {
  const rounded = new Date(value)
  rounded.setSeconds(0, 0)

  const nextStep = Math.ceil(rounded.getMinutes() / 15) * 15
  if (nextStep === 60) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0)
    return rounded
  }

  rounded.setMinutes(nextStep)
  return rounded
}

export const toDateTimeLocalInputValue = (value: Date): string => {
  const localDate = new Date(value.getTime() - value.getTimezoneOffset() * 60000)
  return localDate.toISOString().slice(0, 16)
}

export const fromDateTimeLocalInputValue = (value: string): Date => new Date(value)

/**
 * Converte datetime-local (YYYY-MM-DDTHH:mm) para ISO UTC.
 * Garante armazenamento correto no banco (timestamptz) independente do timezone do usuário.
 * Se o valor já for ISO com offset (Z ou ±HH:mm), retorna como está.
 */
export const toIsoUtcForStorage = (value: string): string => {
  if (!value?.trim()) return value
  const trimmed = value.trim()
  if (trimmed.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(trimmed)) return trimmed
  const d = fromDateTimeLocalInputValue(trimmed)
  if (Number.isNaN(d.getTime())) return value
  return d.toISOString()
}

export const createDefaultCalendarWindow = (
  now: Date = new Date(),
): { startDateTime: string; endDateTime: string } => {
  const start = roundToNextQuarterHour(now)
  const end = addMinutes(start, 30)

  return {
    startDateTime: toDateTimeLocalInputValue(start),
    endDateTime: toDateTimeLocalInputValue(end),
  }
}

/**
 * Cria intervalo de dia inteiro (meia-noite até meia-noite do dia seguinte)
 * a partir de uma string datetime. Usado quando "evento dia inteiro" é marcado.
 */
export const createAllDayRangeFromDateTime = (
  dateTimeStr: string,
): { startDateTime: string; endDateTime: string } => {
  const d = fromDateTimeLocalInputValue(dateTimeStr)
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)
  return {
    startDateTime: toDateTimeLocalInputValue(dayStart),
    endDateTime: toDateTimeLocalInputValue(dayEnd),
  }
}

export const formatShortDate = (value: string): string => {
  const parsed = parseDateOrNull(value)
  if (!parsed) {
    return 'Sem data'
  }
  return SHORT_DATE_FORMATTER.format(parsed)
}

/** Data por extenso em pt-BR (ex.: «01 de dezembro de 2024»). YYYY-MM-DD é calendário local. */
export const formatLongDate = (value: string): string => {
  const parsed = parseDateOrNull(value)
  if (!parsed) {
    return value
  }
  return LONG_DATE_FORMATTER.format(parsed)
}

export const formatShortDateWithYear = (value: string): string => {
  const parsed = parseDateOrNull(value)
  if (!parsed) {
    return 'Sem data'
  }
  return SHORT_DATE_WITH_YEAR_FORMATTER.format(parsed)
}

export const formatDateTime = (value: string): string => {
  const parsed = parseDateOrNull(value)
  if (!parsed) {
    return 'Data não informada'
  }
  return FULL_DATE_TIME_FORMATTER.format(parsed)
}

/** Formata datetime; retorna fallback se valor inválido. Aceita unknown (ex.: dados de API). */
export const formatSafeDateTime = (value: unknown): string => {
  if (!value || !parseDateOrNull(String(value))) return 'Data não informada'
  return formatDateTime(String(value))
}

/** Formata data curta; retorna fallback se valor inválido. */
export const formatSafeShortDate = (value: unknown): string => {
  if (!value || !parseDateOrNull(String(value))) return 'Sem data'
  return formatShortDate(String(value))
}

/** Formata data curta com ano; retorna fallback se valor inválido. */
export const formatSafeShortDateWithYear = (value: unknown): string => {
  if (!value || !parseDateOrNull(String(value))) return 'Sem data'
  return formatShortDateWithYear(String(value))
}

export const formatDayHeader = (value: string): string => {
  const parsed = parseDateOrNull(value)
  if (!parsed) {
    return 'Sem data'
  }
  return DAY_HEADER_FORMATTER.format(parsed)
}

/** Dia da semana por extenso (ex.: «segunda-feira») para exibição em calendários. */
export const formatWeekdayLong = (value: string): string => {
  const parsed = parseDateOrNull(value.slice(0, 10))
  if (!parsed) return ''
  const label = WEEKDAY_LONG_FORMATTER.format(parsed)
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : ''
}

/** Converte ISO date (YYYY-MM-DD) para datetime-local preservando hora existente ou meio-dia. */
export const mergeDateIntoDateTimeLocal = (dateIso: string, existingDateTimeLocal: string): string => {
  const datePart = dateIso.trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return existingDateTimeLocal
  const timePart = /^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/.exec(existingDateTimeLocal.trim())?.[1] ?? '12:00'
  return `${datePart}T${timePart}`
}

export const calendarDayKey = (value: string): string => value.slice(0, 10)

/** Chave de data no fuso local (YYYY-MM-DD) para comparação de "hoje". */
export const toLocalDateKey = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Agrupamento da agenda: mesma data local que filterByDay (isSameDay), não o prefixo UTC do ISO. */
export const calendarLocalDayKey = (iso: string): string => toLocalDateKey(new Date(iso))

/** Interpreta chave YYYY-MM-DD como data local (meio-dia evita bordas de DST). */
export const parseCalendarLocalDayKey = (key: string): Date => {
  const parts = key.split('-').map((x) => parseInt(x, 10))
  const [ys, ms, ds] = parts
  if (!Number.isFinite(ys) || !Number.isFinite(ms) || !Number.isFinite(ds)) return new Date(NaN)
  return new Date(ys, ms - 1, ds, 12, 0, 0)
}

const TIME_ONLY_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

export const formatTimeOnly = (value: string): string => {
  const parsed = parseDateOrNull(value)
  if (!parsed) return '--:--'
  return TIME_ONLY_FORMATTER.format(parsed)
}

export interface CalendarEventForShare {
  title: string
  startDateTime: string
  endDateTime: string
  allDay?: boolean
  location?: string
}

/** Texto formatado para compartilhar evento (WhatsApp, etc.) com ícones. */
export const formatEventShareText = (event: CalendarEventForShare): string => {
  const dateStr = formatShortDateWithYear(event.startDateTime)
  const timeStr = event.allDay
    ? 'Dia inteiro'
    : `${formatTimeOnly(event.startDateTime)} - ${formatTimeOnly(event.endDateTime)}`
  const lines: string[] = [
    '🩺 Sua consulta está agendada',
    '',
    `📅 Data: ${dateStr}`,
    `🕐 Horário: ${timeStr}`,
    `📋 ${event.title}`,
  ]
  if (event.location?.trim()) {
    lines.push(`📍 ${event.location.trim()}`)
  }
  return lines.join('\n')
}

